import { FlaskConical } from 'lucide-react'
import { apptLabSummary } from '../../lib/lab.js'

/**
 * "This visit has lab work" — the flask beside an appointment (Ram, 2026-09-13).
 *
 * Shared by the patient page's appointment table/cards and the clinic-wide booking
 * calendar, so one appointment reads the same wherever it is drawn.
 *
 * 🔑 THE COLOUR IS THE POINT, not the icon. A neutral "has lab" marker answers a
 * question nobody asks; what staff need to know at a glance is whether the piece is
 * IN. So:
 *   rose,
 *   filled  — CARRY-OVER (2026-09-13): a piece from an EARLIER visit that has still
 *             not arrived, flagged on the visits that come after it. This one is not
 *             about this appointment's own lab at all — it fires on a visit with no
 *             lab of its own, which is exactly when nothing else would warn anyone,
 *             and it outranks every state below. See lib/lab.js#carryOverLabsForAppt
 *             for its two deliberate limits (today onward; every remaining visit).
 *   rose    — at least one piece is still out AND the visit day has arrived or passed.
 *             The patient is here (or was) and we have nothing to fit.
 *   amber   — still out, past its กำหนดส่ง but the visit is in the future. Chase it now,
 *             while chasing still helps.
 *   slate   — still out, inside its window. Nothing to do.
 *   emerald — every piece is at the clinic. This is the "all good" state.
 *
 * ⚠️ IT CAN BE WRONG, IN ONE DIRECTION, AND THAT IS ACCEPTED. Only ~24% of labs carry
 * `appointment_id`, so the index also matches on same-patient-same-Bangkok-day, which
 * roughly doubles coverage but is an inference: where a patient has two visits in a day
 * (33 of 297 matched September rows) the piece is flagged on both. Fine for "there is
 * lab work around this visit", NOT fine for anything that counts or bills — see
 * lib/lab.js#buildApptLabIndex.
 *
 * Renders nothing at all when there is no lab, and while the index is still loading —
 * an absent flag must never be readable as "no lab work", so it stays absent either way
 * rather than flashing a grey placeholder onto every row in the list.
 */
// `dot` is the bare-mode fill (an SVG text colour → currentColor), stated rather than
// derived from `cls` (whose first class is a background for `carry`, text for the rest).
const TONES = {
  carry: { cls: 'text-white bg-rose-600 ring-rose-600', dot: 'text-rose-600', word: 'ค้างจากนัดก่อน' },
  overdue: { cls: 'text-rose-600 bg-rose-50 ring-rose-200', dot: 'text-rose-500', word: 'ยังไม่ได้รับ' },
  late: { cls: 'text-amber-600 bg-amber-50 ring-amber-200', dot: 'text-amber-500', word: 'ยังไม่ได้รับ' },
  due: { cls: 'text-slate-500 bg-slate-50 ring-slate-200', dot: 'text-slate-400', word: 'ยังไม่ได้รับ' },
  done: { cls: 'text-emerald-600 bg-emerald-50 ring-emerald-200', dot: 'text-emerald-500', word: 'ได้รับแล้ว' },
}

// The calendar's dot, drawn as ONE svg — disc and symbol share a 24-unit box, so the
// symbol is exactly centred at any size. (A 13px span with an 8px lucide icon and a
// text "!" landed half a pixel left or right depending on where the block fell.)
// Flask = lucide FlaskConical's paths (centred on 12,12), scaled down about the centre.
export function LabDot({ level, size = 16 }) {
  const t = TONES[level] || TONES.due
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={`block flex-none ${t.dot}`} aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="currentColor" />
      <g fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round">
        {level === 'carry' ? (
          <>
            <path d="M12 6.5v7" strokeWidth="3.2" />
            <circle cx="12" cy="17.6" r="0.4" strokeWidth="3.2" />
          </>
        ) : (
          <g transform="translate(12 12) scale(0.58) translate(-12 -12)" strokeWidth="3.6">
            <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2" />
            <path d="M8.5 2h7" />
          </g>
        )}
      </g>
    </svg>
  )
}

// Not a kit <Badge>: the carry-over state is a SOLID rose pill and the calendar needs
// an 11px size, neither of which Badge has (kit gap). Same shape as Badge (rounded-full).
// `bare` is for the calendar's day blocks (16px minimum) — a ringed pill is unreadable
// there. It is a SOLID 16px dot (LabDot) with a white flask (Ram, 2026-10-20): a 10px stroked
// glyph left only a few coloured pixels, so red / grey / amber blurred on the pastel
// blocks. Carry-over puts a "!" in place of the flask, because both reds otherwise
// look the same at this size and carry-over is the one to act on.
export default function ApptLabFlag({ index, appt, size = 'md', bare = false }) {
  const sum = index ? apptLabSummary(index, appt) : null
  if (!sum) return null
  // A carry-over outranks this visit's own pieces however those are doing: a piece
  // that should already be here is the one somebody has to act on today.
  const level = sum.carry ? 'carry' : sum.received ? 'done' : (sum.worst?.level || 'due')
  const t = TONES[level] || TONES.due
  const px = size === 'sm' ? 11 : 13
  // The count on the pill counts what is WRONG when something is wrong, so the
  // number always agrees with the colour.
  const badge = sum.carry || (sum.received ? sum.count : sum.pending)
  const title = [
    sum.carry ? `⚠️ Lab ค้างจากนัดก่อน ${sum.carry} ชิ้น — ควรได้รับก่อนนัดนี้` : null,
    sum.carry ? sum.carryWorst?.label : null,
    sum.count ? `งาน Lab นัดนี้ ${sum.count} ชิ้น` : null,
    sum.count ? (sum.received ? 'ได้รับครบแล้ว' : `ยังไม่ได้รับ ${sum.pending} ชิ้น`) : null,
    sum.count ? sum.worst?.label : null,
  ].filter(Boolean).join(' · ')
  if (bare) {
    return (
      <span title={title} aria-label={title} className="inline-flex">
        <LabDot level={level} />
      </span>
    )
  }
  // A bare flask made staff look twice to work out what it meant, so the pill says
  // so in words (Ram, 2026-09-13). The WORD is constant and the COLOUR carries the
  // state — except a carry-over, which is a different fact and says so.
  const word = sum.carry ? 'Lab ค้าง' : 'มี Lab'
  return (
    <span title={title} aria-label={title}
      className={`inline-flex items-center gap-1 rounded-full ring-1 ring-inset ${t.cls} ${size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-[11.5px]'}`}>
      <FlaskConical size={px} className="flex-none" />
      <span className="font-semibold whitespace-nowrap">{word}{badge > 1 && <span className="tabular-nums"> {badge}</span>}</span>
    </span>
  )
}
